import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import type { VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { cn } from '@/lib/utils';

/*
 * shadcn button, restyled to the prototype's beveled chrome: Silkscreen
 * pixel label, two-tone bevel border, zero radius. The mock only designed
 * hover (brightness) - focus-visible is added deliberately.
 *
 * Variants mirror the mock: `default` is the gray raised button (var(--btn)
 * + panel bevel); `primary` is the warm CTA (#c77817, white label,
 * #ffb968/#6e3c05 bevel). Mock buttons carry no drop shadow - the hard
 * offset shadow belongs to windows/panels only.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-display text-[11px] uppercase tracking-wide transition-[filter] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none hover:brightness-112 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:translate-x-px active:translate-y-px",
  {
    variants: {
      variant: {
        default: 'bg-btn text-titletext bevel',
        primary: 'bg-orange-cta text-orange-cta-tx bevel-orange hover:brightness-110',
        outline: 'bg-panel/70 text-text bevel',
        ghost: 'bg-transparent text-dim hover:text-strong',
        destructive: 'bg-destructive text-destructive-foreground bevel',
        link: 'text-green underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 px-3',
        lg: 'h-10 px-6',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

function Button({
  className,
  variant,
  size,
  asChild = false,
  ...props
}: React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  }) {
  const Comp = asChild ? Slot : 'button';

  return (
    <Comp
      data-slot="button"
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants };
